import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useCookies } from 'react-cookie';
import axios from 'axios';
import { WarningCallout, InsetText, Card } from "nhsuk-react-components";
import { logout, getMeDetails } from "../actions/userActions";
import { USER_LOGIN_SUCCESS } from "../constants/userConstants";
import { CheckTick } from "../components/browserCheck/BrowserCheck";

function TwoFACodeRequestScreen({ history }) {
    const dispatch = useDispatch();
    const [cookies, setCookie] = useCookies(['codeRequestCount', 'codeRequestBlockTime']);

    useEffect(() => {
        document.title = "2-Factor Authentication - MyHeartPassport";
    }, []);

    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const [code, setCode] = useState('');
    const [TFAMessage, setTFAMessage] = useState('');
    const [sendingCode, setSendingEmail] = useState(false);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [isBlocked, setIsBlocked] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [verified, setVerified] = useState(false);

    useEffect(() => {
        // Check if 2FA is already completed
        const isAuthenticated = localStorage.getItem('2fauthenticated') === 'true';
        if (isAuthenticated && userInfo && userInfo.token) {
            history.push("/dashboard");
            return;
        }

        // Only run once on mount - don't re-run when userInfo changes
        const pendingEmail = localStorage.getItem('2faPendingEmail');
        
        if (pendingEmail) {
            setEmail(pendingEmail);
            checkBlockStatus();
            // Don't auto-send code since backend already sent it during login
            setTFAMessage(`A verification code has been sent to ${pendingEmail}`);
            setCodeSent(true);
        } else if (!userInfo || !userInfo.email) {
            history.push("/login");
        } else if (!codeSent) {
            // Only send if we haven't sent already
            setEmail(userInfo.email);
            checkBlockStatus();
            handle2FACodeRequest(userInfo.email);
            setCodeSent(true);
        }
    }, []); // Empty dependency array - only run on mount

    const checkBlockStatus = () => {
        const blockTime = cookies.codeRequestBlockTime;
        if (blockTime && new Date(blockTime) > new Date()) {
            setIsBlocked(true);
            const timeLeft = Math.ceil((new Date(blockTime) - new Date()) / (1000 * 60));
            setTFAMessage(`Too many attempts. Please try again in ${timeLeft} minutes.`);
        } else {
            setIsBlocked(false);
            setCookie('codeRequestCount', 0, { path: '/' });
            setCookie('codeRequestBlockTime', '', { path: '/' });
        }
    };

    const handle2FACodeRequest = async (emailToUse) => {
        if (isBlocked) return;

        const currentCount = cookies.codeRequestCount || 0;
        if (currentCount >= 5) {
            const blockTime = new Date(new Date().getTime() + 60 * 60 * 1000);
            setCookie('codeRequestBlockTime', blockTime.toISOString(), { path: '/' });
            setIsBlocked(true);
            setTFAMessage("Too many attempts. Please try again in 1 hour.");
            return;
        }

        setSendingEmail(true);
        setTFAMessage(`Sending code to ${emailToUse}...`);
        try {
            await axios.post(`/api/v1/auth/sendverificationcode`, {
                "email": emailToUse
            });

            setSendingEmail(false);
            setTFAMessage(`Code successfully sent to ${emailToUse}.`);
            setCookie('codeRequestCount', currentCount + 1, { path: '/' });
        } catch (error) {
            console.error('Error sending 2FA code:', error);
            setSendingEmail(false);
            setTFAMessage("2FA code request failed. Please try again.");
        }
    };

    const handle2FACodeSubmission = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await axios.post(`/api/v1/auth/verifycode`, {
                "email": email,
                "code": code
            });

            // Backend now returns full LoginResponseDto with JWT
            const userData = response.data;
            
            setLoading(false);
            setVerified(true);
            setTFAMessage("2FA code verified successfully. Redirecting shortly...");
            
            // Update Redux store and localStorage with full user info
            dispatch({
                type: USER_LOGIN_SUCCESS,
                payload: userData
            });
            
            localStorage.setItem("userInfo", JSON.stringify(userData));
            localStorage.setItem("2fauthenticated", "true");
            localStorage.removeItem("2faPendingEmail");

            // Fetch user profile details
            await dispatch(getMeDetails());

            setTimeout(() => {
                history.push("/dashboard");
            }, 2000);

        } catch (error) {
            setLoading(false);
            setVerified(false);
            setTFAMessage("❌ 2FA code verification failed.");
        }
    };

    const handleLogout = () => {
        dispatch(logout());
    };

    return (
        <div className="nhsuk-width-container">
            <main className="nhsuk-main-wrapper" id="maincontent" role="main">
                <div className="nhsuk-grid-row">
                    <div className="nhsuk-grid-column-two-thirds" style={{ margin: "0 auto", float: "none" }}>
                        <Card style={{ 
                            boxShadow: "0 4px 8px rgba(0,0,0,0.05)", 
                            border: "1px solid #f0f4f5",
                            borderRadius: "8px"
                        }}>
                            <Card.Content>
                                <div style={{ 
                                    display: "flex", 
                                    justifyContent: "space-between", 
                                    alignItems: "center",
                                    marginBottom: "24px",
                                    borderBottom: "1px solid #f0f4f5",
                                    paddingBottom: "16px"
                                }}>
                                    <h1 style={{ margin: 0, fontSize: "1.5rem" }}>
                                        2-Factor Authentication
                                    </h1>
                                </div>
                                
                                <InsetText>
                                    <p>
                                        Please enter the code sent to your email address <strong>{userInfo?.email}</strong> to continue.
                                        If you haven't received the code, please check your spam folder or click the "Send/Resend code" button below.
                                        Note that the code is case-sensitive, so make sure to enter it exactly as it appears in the email.
                                    </p>
                                </InsetText>
                                
                                {TFAMessage && (
                                    <div
                                        className={verified ? "browser-check browser-check--ok" : undefined}
                                        style={verified ? undefined : {
                                            padding: "12px 16px",
                                            margin: "16px 0",
                                            background: "#f0f4f5",
                                            borderRadius: "4px",
                                            borderLeft: TFAMessage.includes("❌") ? "4px solid #d5281b" : "4px solid #005eb8"
                                        }}
                                    >
                                        {verified && (
                                            <span className="browser-check__mark" aria-hidden="true">
                                                <CheckTick />
                                            </span>
                                        )}
                                        <p style={{ margin: 0 }}>{TFAMessage}</p>
                                    </div>
                                )}

                                <form onSubmit={handle2FACodeSubmission} className="nhsuk-form-group">
                                    <div className="nhsuk-form-group">
                                        <label className="nhsuk-label" htmlFor="code">
                                            Verification Code
                                        </label>
                                        <input
                                            className="nhsuk-input"
                                            id="code"
                                            name="code"
                                            type="text"
                                            onChange={(e) => setCode(e.target.value)}
                                            value={code}
                                            required
                                            style={{ 
                                                borderRadius: "4px", 
                                                fontSize: "1.2rem",
                                                letterSpacing: "0.1em" 
                                            }}
                                        />
                                    </div>

                                    <button 
                                        className="nhsuk-button" 
                                        data-module="nhsuk-button" 
                                        type="submit" 
                                        style={{ 
                                            width: '100%', 
                                            marginTop: '24px',
                                            borderRadius: '4px',
                                            fontWeight: 'bold'
                                        }} 
                                        disabled={loading}
                                    >
                                        {loading ? 'Verifying...' : 'Verify Code'}
                                    </button>
                                </form>
                                
                                <div style={{ 
                                    display: "flex", 
                                    justifyContent: "space-between", 
                                    alignItems: "center",
                                    marginTop: "24px",
                                    borderTop: "1px solid #f0f4f5",
                                    paddingTop: "16px"
                                }}>
                                    <a 
                                        href="#" 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handle2FACodeRequest(email);
                                        }}
                                        className="nhsuk-link" 
                                        style={{ 
                                            pointerEvents: sendingCode || isBlocked ? "none" : "auto",
                                            opacity: sendingCode || isBlocked ? 0.7 : 1,
                                            fontWeight: "bold"
                                        }}
                                    >
                                        {sendingCode ? 'Sending code...' : isBlocked ? 'Locked' : 'Send/Resend code'}
                                    </a>
                                    
                                    <Link 
                                        to="/" 
                                        onClick={handleLogout}
                                        className="nhsuk-link"
                                    >
                                        Cancel and Logout
                                    </Link>
                                </div>
                            </Card.Content>
                        </Card>

                        <p style={{ 
                            marginTop: "24px", 
                            fontSize: "0.9rem", 
                            textAlign: "center",
                            color: "#4c6272"
                        }}>
                            By using this service you are agreeing to our <a href="https://www.bartshealth.nhs.uk/privacy" className="nhsuk-link">terms of use</a> and <a href="https://www.bartshealth.nhs.uk/privacy" className="nhsuk-link">privacy policy</a>.
                        </p>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default TwoFACodeRequestScreen;